import { syntheticDemoPortalsEnabled } from '../../../shared/config/api-origin';
import { productFieldDifferences } from '../domain/product-form';
import type { ProductDemoDraft } from '../fixtures/product-submission-demo';
import { productDemoStatusLabels } from '../fixtures/product-submission-demo';
import { PROVIDER_BASE } from '../routes';
import type { ProductSubmissionSession } from './ProductSubmissionPrototype';

function DraftDetail({
  record,
  session,
}: {
  record: ProductDemoDraft;
  session: ProductSubmissionSession;
}) {
  const differences = productFieldDifferences(record.baseline, record.fields);
  return (
    <article className="product-demo-submission-card">
      <header>
        <div>
          <h3>{record.fields.name}</h3>
          <p>
            {record.fields.model || 'UNKNOWN'} · {record.version} ·{' '}
            {record.origin === 'SESSION' ? '本次会话' : '06 独立静态反馈样例'}
          </p>
        </div>
        <span className={`product-demo-status product-demo-status-${record.status}`}>
          {productDemoStatusLabels[record.status]}
        </span>
      </header>
      <p className="product-demo-draft-id">
        demoProductDraftId：{record.demoProductDraftId} · 非正式产品身份
      </p>
      {record.feedback && (
        <section className="product-demo-feedback">
          <strong>退回反馈 · 独立预置演示</strong>
          <p>{record.feedback}</p>
          <p>修订理由：{record.fields.reason || '未知 · 待补充'}</p>
        </section>
      )}
      <dl className="product-demo-record-data">
        <div>
          <dt>资料来源</dt>
          <dd>{record.fields.source || '未知 · 待补充'}</dd>
        </div>
        <div>
          <dt>用途 / 厂家声明</dt>
          <dd>
            {record.fields.purpose || '未知'} / {record.fields.claims || '未知'}
          </dd>
        </div>
        <div>
          <dt>限制说明</dt>
          <dd>{record.fields.limits || '未知 · 尚未说明'}</dd>
        </div>
        <div>
          <dt>附件</dt>
          <dd>
            {record.fields.files.map((file) => `${file.name}（待上传）`).join('；') ||
              '待上传 · 未选择文件'}
          </dd>
        </div>
      </dl>
      <details open={record.status === 'CHANGES_REQUESTED'}>
        <summary>查看 v1 / {record.version} 字段差异（v1 只读）</summary>
        <div className="product-demo-comparison">
          <div className="product-demo-comparison-head">
            <span>字段</span>
            <span>原 v1 · 只读样例</span>
            <span>{record.version} · 拟议资料</span>
          </div>
          {differences.length ? (
            differences.map((change) => (
              <div key={change.label}>
                <strong>{change.label}</strong>
                <span>{change.before}</span>
                <span>{change.after}</span>
              </div>
            ))
          ) : (
            <p>暂无字段变化 · 原 v1 保持不变</p>
          )}
        </div>
      </details>
      <div className="product-demo-record-actions">
        <button
          className="portal-button"
          disabled={record.status === 'PENDING_REVIEW'}
          onClick={() => session.openDraft(record)}
        >
          {record.status === 'CHANGES_REQUESTED' ? '根据反馈修订（演示）' : '编辑本地草稿'}
        </button>
        <button
          className="portal-button"
          disabled={record.status !== 'DRAFT'}
          onClick={() => session.submit(record)}
        >
          模拟提交审核（不发送到08）
        </button>
        <button
          className="portal-button product-demo-secondary"
          disabled={record.status !== 'DRAFT'}
          onClick={() => session.remove(record)}
        >
          删除本地草稿
        </button>
        {record.baselineFixtureId && (
          <a href={`${PROVIDER_BASE}/products/${record.baselineFixtureId}?synLive=0`}>
            返回原 W1 产品详情 ›
          </a>
        )}
      </div>
      <p className="product-demo-help">
        待审核仅本端演示状态，未送达管理端。没有批准发布操作，不改变 06 Owner 或 05 资产。
      </p>
    </article>
  );
}
export function MyProductSubmissions({
  session,
  selected,
}: {
  session: ProductSubmissionSession;
  selected?: ProductDemoDraft;
}) {
  if (!syntheticDemoPortalsEnabled()) return null;
  const records = selected
    ? [selected]
    : session.records.filter(
        (record) =>
          session.submissionFilter === '全部' || record.status === session.submissionFilter,
      );
  return (
    <section className="product-demo-submissions" aria-label="本端产品草稿与演示反馈">
      <div className="product-demo-section-heading">
        <h2>{selected ? '本地产品资料详情' : '本端会话提交与反馈'}</h2>
        {!selected && (
          <label>
            演示状态
            <select
              aria-label="筛选本端提交状态"
              value={session.submissionFilter}
              onChange={(e) => session.setSubmissionFilter(e.target.value)}
            >
              <option value="全部">全部</option>
              {Object.entries(productDemoStatusLabels).map(([status, label]) => (
                <option key={status} value={status}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <p className="product-demo-help">
        以下记录独立于 08 队列。新草稿刷新即丢失；静态退回样例仅在刷新后重新初始化。
      </p>
      {records.map((record) => (
        <DraftDetail key={record.demoProductDraftId} record={record} session={session} />
      ))}
      {records.length === 0 && (
        <div className="provider-empty">
          <h3>暂无匹配的本端提交</h3>
          <p>切换筛选条件或新建演示产品。</p>
          <button className="portal-button" onClick={session.openNew}>
            新增产品（演示）
          </button>
        </div>
      )}
    </section>
  );
}
